import React, { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { Users, Plus, Pencil, ArrowLeft } from 'lucide-react'
import DashboardLayout from '../layouts/DashboardLayout'

// The external-specialist directory. Keeping recipients here (rather than re-typing an address on
// every referral) is the main guard against emailing a clinical PDF to the wrong person.
const BLANK = { name: '', discipline: '', practice_name: '', email: '', phone: '', hpcsa_number: '', address: '' }

export default function Specialists({ specialists = [], disciplines = [] }) {
  const [adding, setAdding] = useState(false)
  const [editId, setEditId] = useState(null)
  const [form, setForm] = useState(BLANK)
  const [busy, setBusy] = useState(false)
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }))

  const input = 'w-full rounded-lg border border-brand-border bg-white px-3 py-2 text-sm focus:border-brand-primary focus:outline-none'

  const submit = () => {
    if (!form.name.trim()) { window.alert("The specialist's name is required."); return }
    setBusy(true)
    const done = { onFinish: () => { setBusy(false); setAdding(false); setEditId(null); setForm(BLANK) } }
    if (editId) router.patch(`/specialists/${editId}`, { specialist: form }, done)
    else router.post('/specialists', { specialist: form }, done)
  }

  const startEdit = (s) => {
    setEditId(s.id); setAdding(false)
    setForm({ name: s.name || '', discipline: s.discipline || '', practice_name: s.practice_name || '',
      email: s.email || '', phone: s.phone || '', hpcsa_number: s.hpcsa_number || '', address: s.address || '' })
  }

  const Form = () => (
    <div className="mb-4 rounded-xl border border-brand-border bg-white p-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div><label className="mb-1 block text-xs text-brand-muted">Name *</label><input className={input} value={form.name} onChange={(e) => set('name', e.target.value)} placeholder="Dr J. Naidoo" /></div>
        <div><label className="mb-1 block text-xs text-brand-muted">Discipline</label>
          <select className={input} value={form.discipline} onChange={(e) => set('discipline', e.target.value)}>
            <option value="">—</option>
            {disciplines.map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
          </select>
        </div>
        <div><label className="mb-1 block text-xs text-brand-muted">Practice / rooms</label><input className={input} value={form.practice_name} onChange={(e) => set('practice_name', e.target.value)} /></div>
        <div><label className="mb-1 block text-xs text-brand-muted">Email</label><input className={input} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="rooms@…" /></div>
        <div><label className="mb-1 block text-xs text-brand-muted">Phone</label><input className={input} value={form.phone} onChange={(e) => set('phone', e.target.value)} /></div>
        <div><label className="mb-1 block text-xs text-brand-muted">HPCSA</label><input className={input} value={form.hpcsa_number} onChange={(e) => set('hpcsa_number', e.target.value)} /></div>
        <div className="sm:col-span-2"><label className="mb-1 block text-xs text-brand-muted">Address</label><input className={input} value={form.address} onChange={(e) => set('address', e.target.value)} /></div>
      </div>
      <div className="mt-3 flex gap-2">
        <button onClick={submit} disabled={busy} className="rounded-lg bg-brand-primary px-4 py-2 text-sm font-semibold text-white hover:bg-brand-primary-dark disabled:opacity-60">
          {busy ? 'Saving…' : editId ? 'Save changes' : 'Add specialist'}
        </button>
        <button onClick={() => { setAdding(false); setEditId(null); setForm(BLANK) }} className="rounded-lg border border-brand-border px-4 py-2 text-sm text-brand-ink-soft hover:bg-brand-surface">Cancel</button>
      </div>
    </div>
  )

  return (
    <DashboardLayout>
      <Link href="/referrals" className="mb-4 inline-flex items-center gap-1 text-sm text-brand-muted hover:text-brand-ink">
        <ArrowLeft size={14} /> Referrals
      </Link>

      <div className="mb-6 flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-brand-primary">
          <Users size={18} className="text-white" />
        </div>
        <div>
          <h1 className="text-xl font-semibold text-brand-ink">Specialist directory</h1>
          <p className="text-sm text-brand-muted">Referral recipients — saved once, then one click on every referral</p>
        </div>
        {!adding && !editId && (
          <button onClick={() => { setAdding(true); setForm(BLANK) }} className="ml-auto inline-flex items-center gap-1.5 rounded-lg bg-brand-primary px-3 py-2 text-sm font-semibold text-white hover:bg-brand-primary-dark">
            <Plus size={15} /> Add specialist
          </button>
        )}
      </div>

      {(adding || editId) && <Form />}

      <div className="rounded-xl border border-brand-border bg-white">
        {specialists.length === 0 && <p className="px-4 py-3 text-xs text-brand-muted">No specialists yet — add the ones you refer to most.</p>}
        {specialists.map((s) => (
          <div key={s.id} className="flex flex-wrap items-center gap-2 border-b border-brand-border/50 px-4 py-3 last:border-0">
            <span className="text-sm font-medium text-brand-ink">{s.name}</span>
            <span className="text-xs text-brand-muted">{s.discipline_label || '—'}</span>
            {s.practice_name && <span className="text-xs text-brand-muted">· {s.practice_name}</span>}
            {s.email
              ? <span className="text-xs text-brand-ink-soft">· {s.email}</span>
              : <span className="text-xs text-amber-600">· no email (can't send by email)</span>}
            <span className="ml-auto flex items-center gap-3">
              {s.referral_count > 0 && <span className="text-xs text-brand-muted">{s.referral_count} referral{s.referral_count === 1 ? '' : 's'}</span>}
              <button onClick={() => startEdit(s)} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-primary hover:underline">
                <Pencil size={12} /> Edit
              </button>
            </span>
          </div>
        ))}
      </div>
    </DashboardLayout>
  )
}
